import { useState, useRef, useEffect, useCallback } from "react";
import textToSpeech from "../utils/textToSpeech";

const useVoiceOutput = ({
  content,
  isPlaceholder,
  enableVoiceOutput,
  autoPlayVoice = true,
  isAssistant,
  onVoiceOutputEnd,
  /** Called once when TTS audio starts playing (use to stop mic input). */
  onVoiceOutputStart,
  isStreamingVop = false,
  isAzureVop = false,
}) => {
  const isVopActive = isStreamingVop || isAzureVop;
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);
  const [isVoiceUnavailable, setIsVoiceUnavailable] = useState(false);
  const spokenIndexRef = useRef(0);
  const audioQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);
  const isPlaceholderRef = useRef(isPlaceholder);
  const micTriggeredRef = useRef(false);
  const voiceStartFiredRef = useRef(false);

  const autoPlayEnabledRef = useRef(autoPlayVoice);
  /** Track previous isPlaceholder to detect new-message transitions. */
  const prevIsPlaceholderRef = useRef(isPlaceholder);

  useEffect(() => {
    // Detect new message: isPlaceholder transitions false → true
    if (isPlaceholder && !prevIsPlaceholderRef.current) {
      setIsVoiceUnavailable(false);
      spokenIndexRef.current = 0;
      micTriggeredRef.current = false;
      voiceStartFiredRef.current = false;
      audioQueueRef.current = [];
      isSpeakingRef.current = false;
    }
    prevIsPlaceholderRef.current = isPlaceholder;
    isPlaceholderRef.current = isPlaceholder;
  }, [isPlaceholder]);

  const processAudioQueue = useCallback(() => {
    if (isSpeakingRef.current || audioQueueRef.current.length === 0) return;

    const chunk = audioQueueRef.current.shift();
    isSpeakingRef.current = true;
    setIsVoicePlaying(true);
    if (typeof window !== "undefined") {
      window.currentSpeakingText = content || "";
    }

    // Fire onVoiceOutputStart once before the first chunk plays
    if (!voiceStartFiredRef.current && onVoiceOutputStart) {
      voiceStartFiredRef.current = true;
      onVoiceOutputStart();
    }

    textToSpeech.speak(chunk, {
      interrupt: false, // Do not cancel the engine between streaming chunks
      onStart: () => setIsVoicePlaying(true),
      onEnd: () => {
        isSpeakingRef.current = false;
        if (audioQueueRef.current.length > 0) {
          processAudioQueue();
        } else {
          setIsVoicePlaying(false);
          if (typeof window !== "undefined") {
            window.currentSpeakingText = "";
          }
          // Trigger mic ONLY if stream is done and we haven't already
          if (!isPlaceholderRef.current && onVoiceOutputEnd && !micTriggeredRef.current) {
            micTriggeredRef.current = true;
            onVoiceOutputEnd();
          }
        }
      },
      onError: () => {
        isSpeakingRef.current = false;
        setIsVoicePlaying(false);
        setIsVoiceUnavailable(true);
        if (typeof window !== "undefined") {
          window.currentSpeakingText = "";
        }
      }
    });
  }, [onVoiceOutputEnd, onVoiceOutputStart, content]);

  // Stream chunks to audio queue as they arrive
  useEffect(() => {
    if (isVopActive || !autoPlayEnabledRef.current || !enableVoiceOutput || !isAssistant || !content || content === "Loading...") {
      return;
    }

    const newText = content.substring(spokenIndexRef.current);

    let chunkRaw = "";
    if (!isPlaceholder) {
      // Stream finished, take whatever is left
      chunkRaw = newText;
    } else {
      // Still streaming, look for sentence boundary (greedy match up to the last boundary)
      const match = newText.match(/.*[.?!](\s+|$)|.*\n+/s);
      if (match) {
        chunkRaw = match[0];
      }
    }

    if (chunkRaw.length > 0) {
      spokenIndexRef.current += chunkRaw.length;
      const cleanChunk = chunkRaw.trim();

      // Only push if there's actual speakable text to avoid stalling the queue
      const speakableText = textToSpeech.cleanTextForSpeech(cleanChunk).trim();
      const hasAlphanumeric = /[\p{L}\p{N}]/u.test(speakableText);

      if (speakableText.length > 0 && hasAlphanumeric) {
        audioQueueRef.current.push(cleanChunk);
        processAudioQueue();
      } else if (!isPlaceholder && audioQueueRef.current.length === 0 && !isSpeakingRef.current) {
        // Stream finished, this chunk was silent, queue is empty, trigger
        // End of stream + no more queue = we are fully done playing everything
        setIsVoicePlaying(false);
        if (onVoiceOutputEnd && !micTriggeredRef.current) {
          micTriggeredRef.current = true;
          onVoiceOutputEnd();
        }
      }
    } else if (!isPlaceholder && audioQueueRef.current.length === 0 && !isSpeakingRef.current) {
      // Stream finished, no new text, queue is empty, and not speaking.
      setIsVoicePlaying(false);
      if (onVoiceOutputEnd && !micTriggeredRef.current) {
        micTriggeredRef.current = true;
        onVoiceOutputEnd();
      }
    }
  }, [content, isPlaceholder, enableVoiceOutput, isAssistant, processAudioQueue, onVoiceOutputEnd, isVopActive]);

  /** Toggle TTS play/pause manually */
  const handleVoiceToggle = useCallback(() => {
    if (isVoicePlaying) {
      audioQueueRef.current = [];
      isSpeakingRef.current = false;
      textToSpeech.stop();
      setIsVoicePlaying(false);
      autoPlayEnabledRef.current = false;
      if (typeof window !== "undefined") {
        window.currentSpeakingText = "";
      }
    } else {
      setIsVoiceUnavailable(false);
      micTriggeredRef.current = true; // Disable auto-mic if manually played
      autoPlayEnabledRef.current = true;
      spokenIndexRef.current = content.length;
      isSpeakingRef.current = true;
      setIsVoicePlaying(true);
      if (typeof window !== "undefined") {
        window.currentSpeakingText = content || "";
      }

      textToSpeech.speak(content || "", {
        onStart: () => setIsVoicePlaying(true),
        onEnd: () => {
          isSpeakingRef.current = false;
          setIsVoicePlaying(false);
          if (typeof window !== "undefined") {
            window.currentSpeakingText = "";
          }
        },
        onError: () => {
          isSpeakingRef.current = false;
          setIsVoicePlaying(false);
          setIsVoiceUnavailable(true);
          if (typeof window !== "undefined") {
            window.currentSpeakingText = "";
          }
        },
      });
    }
  }, [isVoicePlaying, content]);

  // Stop TTS when component unmounts
  useEffect(() => {
    return () => {
      if (enableVoiceOutput && !isVopActive) {
        textToSpeech.stop();
        if (typeof window !== "undefined") {
          window.currentSpeakingText = "";
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableVoiceOutput, isVopActive]);

  return {
    isVoicePlaying,
    isVoiceUnavailable,
    handleVoiceToggle
  };
};

export default useVoiceOutput;
